import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import LineUserSelector from './LineUserSelector';
import EmailListSelector from './EmailListSelector';
import UserSelector from './UserSelector';

const ParameterEditor = ({ schema, values, onChange }) => {
  const [params, setParams] = useState(values || {});
  const [schemaObj, setSchemaObj] = useState(null);
  const [paramDefinitions, setParamDefinitions] = useState([]);

  useEffect(() => {
    try {
      // Handle both old schema format (JSON Schema) and new parameter definitions format
      if (Array.isArray(schema)) {
        // New format: array of ParameterDefinition objects
        setParamDefinitions(schema);
        setSchemaObj(null);
      } else {
        // Old format: JSON Schema object
        const parsed = typeof schema === 'string' ? JSON.parse(schema) : schema;
        setSchemaObj(parsed);
        setParamDefinitions([]);
      }
    } catch (error) {
      console.error('Failed to parse schema:', error);
    }
  }, [schema]);

  useEffect(() => {
    if (values) {
      setParams(values);
    }
  }, [values]);

  const handleChange = (key, value) => {
    const newParams = { ...params, [key]: value };
    setParams(newParams);
    if (onChange) {
      onChange(newParams);
    }
  };

  // Render new parameter definitions format
  const renderParameterDefinitions = () => {
    if (paramDefinitions.length === 0) {
      return (
        <div className="text-sm text-gray-500">
          No parameters required for this endpoint.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {paramDefinitions.map(param => {
          // Check dependencies
          if (param.dependsOn) {
            const dependentValue = params[param.dependsOn.field];
            if (dependentValue !== param.dependsOn.value) {
              return null; // Don't render if dependency not met
            }
          }

          return (
            <div key={param.name}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {param.label}
                {param.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              
              {renderParameterField(param)}
              
              {param.description && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {param.description}
                </p>
              )}
              
              {param.helpText && (
                <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                  💡 {param.helpText}
                </p>
              )}
            </div>
          );
        })}

        {paramDefinitions.some(p => p.required) && (
          <div className="flex items-start space-x-2 text-xs text-gray-500 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
            <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
            <span>
              Required parameters: {paramDefinitions.filter(p => p.required).map(p => p.label).join(', ')}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderParameterField = (param) => {
    const value = params[param.name] !== undefined ? params[param.name] : (param.defaultValue || '');

    // Check for standard keywords first
    if (param.keyword) {
      switch (param.keyword) {
        case 'EMAIL_LIST':
          return (
            <EmailListSelector
              value={value}
              onChange={(val) => handleChange(param.name, val)}
              multiple={param.multiple !== false}
              required={param.required}
            />
          );
        
        case 'LINE_ID_LIST':
          return (
            <LineUserSelector
              value={value}
              onChange={(val) => handleChange(param.name, val)}
              multiple={param.multiple !== false}
              required={param.required}
            />
          );
        
        case 'USER_ID':
        case 'USER_ID_LIST':
          return (
            <UserSelector
              value={value}
              onChange={(val) => handleChange(param.name, val)}
              multiple={param.keyword === 'USER_ID_LIST'}
              required={param.required}
            />
          );
      }
    }

    // Standard field types
    switch (param.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            placeholder={param.placeholder || ''}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            placeholder={param.placeholder || ''}
            rows={param.rows || 3}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(param.name, parseFloat(e.target.value) || '')}
            placeholder={param.placeholder || ''}
            min={param.validation?.min}
            max={param.validation?.max}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
        );

      case 'boolean':
        return (
          <div className="mt-1">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleChange(param.name, e.target.checked)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
          </div>
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          >
            <option value="">Select...</option>
            {param.options && param.options.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
        );

      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            placeholder={param.placeholder || ''}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
        );

      case 'email_list':
        return (
          <EmailListSelector
            value={value}
            onChange={(val) => handleChange(param.name, val)}
            multiple={param.multiple !== false}
            required={param.required}
          />
        );

      case 'line_id_list':
        return (
          <LineUserSelector
            value={value}
            onChange={(val) => handleChange(param.name, val)}
            multiple={param.multiple !== false}
            required={param.required}
          />
        );

      case 'user_id':
        return (
          <UserSelector
            value={value}
            onChange={(val) => handleChange(param.name, val)}
            multiple={param.multiple === true}
            required={param.required}
          />
        );

      case 'json':
        return (
          <textarea
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleChange(param.name, parsed);
              } catch (error) {
                // Keep the string value if not valid JSON yet
                handleChange(param.name, e.target.value);
              }
            }}
            rows={param.rows || 4}
            placeholder={param.placeholder || '{}'}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm font-mono text-xs"
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(param.name, e.target.value)}
            placeholder={param.placeholder || ''}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
        );
    }
  };

  // Check if we should render new format
  if (paramDefinitions.length > 0) {
    return renderParameterDefinitions();
  }

  // Legacy format
  if (!schemaObj || !schemaObj.properties) {
    return (
      <div className="text-sm text-gray-500">
        No parameters required for this endpoint.
      </div>
    );
  }

  const renderField = (key, property) => {
    const isRequired = schemaObj.required && schemaObj.required.includes(key);
    const value = params[key] || property.default || '';

    switch (property.type) {
      case 'string':
        if (property.enum) {
          // Enum - select dropdown
          return (
            <select
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
            >
              <option value="">Select...</option>
              {property.enum.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        } else if (property.format === 'email') {
          // Email input
          return (
            <input
              type="email"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
              placeholder={property.description || ''}
            />
          );
        } else {
          // Regular text input
          return (
            <input
              type="text"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
              placeholder={property.description || ''}
            />
          );
        }

      case 'number':
      case 'integer':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(key, parseFloat(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
            placeholder={property.description || ''}
          />
        );

      case 'boolean':
        return (
          <div className="mt-1">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleChange(key, e.target.checked)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
          </div>
        );

      case 'array':
        // Array - Check if this is a LINE recipients parameter
        const isLineRecipients = key.toLowerCase().includes('recipient') && 
                                  (property.description?.toLowerCase().includes('line') || 
                                   property.description?.toLowerCase().includes('user id'));
        
        if (isLineRecipients) {
          // Use special LINE user selector
          return (
            <LineUserSelector
              value={value}
              onChange={(selectedIds) => handleChange(key, selectedIds)}
              multiple={true}
              required={isRequired}
            />
          );
        }
        
        // Regular array - handle as comma-separated values
        const arrayValue = Array.isArray(value) ? value.join(', ') : value;
        return (
          <div>
            <input
              type="text"
              value={arrayValue}
              onChange={(e) => {
                const items = e.target.value.split(',').map(item => item.trim()).filter(Boolean);
                handleChange(key, items);
              }}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
              placeholder={property.description || 'Comma-separated values'}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Enter values separated by commas
            </p>
          </div>
        );

      case 'object':
        // Object - handle as JSON
        return (
          <textarea
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleChange(key, parsed);
              } catch (error) {
                // Keep the string value if not valid JSON yet
                handleChange(key, e.target.value);
              }
            }}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm font-mono text-xs"
            placeholder={'{\n  "key": "value"\n}'}
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
            placeholder={property.description || ''}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      {Object.entries(schemaObj.properties).map(([key, property]) => {
        const isRequired = schemaObj.required && schemaObj.required.includes(key);
        
        return (
          <div key={key}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              {isRequired && <span className="text-red-500 ml-1">*</span>}
            </label>
            
            {renderField(key, property)}
            
            {property.description && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {property.description}
              </p>
            )}
          </div>
        );
      })}

      {schemaObj.required && schemaObj.required.length > 0 && (
        <div className="flex items-start space-x-2 text-xs text-gray-500 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
          <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
          <span>
            Required parameters: {schemaObj.required.join(', ')}
          </span>
        </div>
      )}
    </div>
  );
};

export default ParameterEditor;

